import { ContractFormInfosDto } from '../../../services/dtos/form/contract-form-infos.dto';
import React, { useState } from 'react';
import { object, string } from 'yup';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Drawer,
    Grid,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import InputForm from '../../commun/input-form';
import { Form, Formik } from 'formik';
import './contract-attachement.form.scss';
import { ERROR_MAIL } from '../../../utils/constants.utils';
import NumericPad from '../../commun/numeric-pad';

//Props d'entrée de la fonction ContractAttachementForm
type Props = {
    handlerSubmit: (e: ContractFormInfosDto) => void;
    handlerCancel: () => void;
    validate: boolean;
    title?: string;
    logged: boolean;
};

/**
 * Formulaire de rattachement de contrat
 * @constructor
 * @param props les props
 */
function ContractAttachementForm(props: Props) {
    const [showPad, setShowPad] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Schéma de validation des données
    // champs :
    // referenceFacture, referenceContrat, montantFacture, email
    const validationSchema = object({
        referenceFacture: string()
            .trim()
            .required("Merci d'entrer votre numéro de facture.")
            .matches(/^\S+$/, 'Les espaces ne sont pas autorisés.'),
        referenceContrat: string()
            .trim()
            .required("Merci d'entrer votre numéro d'abonnement.")
            .matches(/^\S+$/, 'Les espaces ne sont pas autorisés.'),
        montantFacture: string()
            .required("Merci d'entrer le montant de votre facture.")
            .matches(/^-?\d+([.,]\d+)?$/, 'Montant invalide (utilisez . ou ,)'),
        email: string().when(() =>
            props.logged
                ? string().notRequired()
                : string()
                      .required("Merci d'entrer une adresse email")
                      .email(ERROR_MAIL)
                      .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, ERROR_MAIL)
        ),
    });

    // Valeurs par défaut
    const iniValues = {
        referenceFacture: '',
        referenceContrat: '',
        montantFacture: '',
        email: '',
    };

    /**
     * Méthode qui permet de gérer la soumission du formulaire
     * @param values données du formulaire
     */
    const handleFormSubmit = (values: any) => {
        //Conversion du montant en number
        const data = {
            ...values,
            referenceContrat: values.referenceContrat?.trim(),
            referenceFacture: values.referenceFacture?.trim(),
            montantFacture: Number(values.montantFacture.replace(',', '.')),
        };
        props.handlerSubmit(data);
    };

    /**
     * Gestion des touches du clavier numérique personnalisé
     */
    const handlePadKeyPress = (key: string, currentVal: string, setFieldValue: any) => {
        const MAX_AMOUNT_LENGTH = 15;
        // Gestion de l'effacement
        if (key === 'Effacer') {
            setFieldValue('montantFacture', currentVal.slice(0, -1));
            return;
        }

        // 1. Gestion du signe moins (doit être au début)
        if (key === '-') {
            if (currentVal.includes('-')) {
                return; // Déjà présent
            }
            setFieldValue('montantFacture', '-' + currentVal);
            return;
        }

        // 2. Gestion des séparateurs (point ou virgule)
        if (key === '.' || key === ',') {
            if (currentVal.includes('.') || currentVal.includes(',')) {
                return; // Déjà un séparateur
            }
            if (currentVal === '' || currentVal === '-') {
                setFieldValue('montantFacture', currentVal + '0' + key); // Ajoute un 0 devant
                return;
            }
        }

        // 3. Limitation de la longueur (optionnel, ex: 15 chars)
        if (currentVal.length > MAX_AMOUNT_LENGTH) {
            return;
        }

        setFieldValue('montantFacture', currentVal + key);
    };

    return (
        //Affichage en deux parties
        //Première : formulaire
        //Seconde : image
        <Card className="cardContract">
            {/*header*/}
            <CardHeader
                title={props.title ? props.title : 'Ajouter un contrat à votre compte'}
                subheader={'Munissez-vous de votre dernière facture et remplissez le formulaire ci-dessous :'}
            />
            {/*formik*/}
            <Formik initialValues={iniValues} onSubmit={handleFormSubmit} validationSchema={validationSchema}>
                {({ values, setFieldValue }) => (
                    /*formulaire*/
                    <Form>
                        {/*content*/}
                        <CardContent>
                            <Grid container spacing={2}>
                                {/*obligations*/}
                                <Grid item xs={12} md={12} sx={{ textAlign: 'right' }}>
                                    <Typography variant={'subtitle2'}>* : champs obligatoires</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    {/*Champ numéro d'abonnement*/}
                                    <InputForm
                                        required
                                        label={
                                            // label complexe
                                            <>
                                                <Typography component="span" sx={{ color: 'red' }}>
                                                    1 -{' '}
                                                </Typography>
                                                Numéro d'abonnement
                                            </>
                                        }
                                        id={'reference-contrat'}
                                        name={'referenceContrat'}
                                        placeholder={'Exemple : TRO42900037'}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    {/*Champ numéro de facture*/}
                                    <InputForm
                                        required
                                        label={
                                            // label complexe
                                            <>
                                                <Typography component="span" sx={{ color: 'red' }}>
                                                    2 -{' '}
                                                </Typography>
                                                Numéro de facture
                                            </>
                                        }
                                        id={'reference-facture'}
                                        name={'referenceFacture'}
                                        placeholder={'Exemple : 6451237984561'}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    {/*Champ montant de facture - CUSTOM PAD ON MOBILE ONLY */}
                                    <div onClick={() => isMobile && setShowPad(true)}>
                                        <InputForm
                                            required
                                            // Le type text est important pour l'affichage, mais on bloque l'édition native
                                            type={'text'}
                                            allowedRegex={/^[\d.,-]*$/}
                                            label={
                                                <>
                                                    <Typography component="span" sx={{ color: 'red' }}>
                                                        3 -{' '}
                                                    </Typography>
                                                    Montant de la facture
                                                </>
                                            }
                                            id={'montant-facture'}
                                            name={'montantFacture'}
                                            placeholder={'Ex: 123.45'}
                                            // On ne bloque le clavier que sur mobile
                                            InputProps={{
                                                readOnly: isMobile,
                                            }}
                                            autoComplete={isMobile ? 'off' : 'on'}
                                        />
                                    </div>
                                    {/* Drawer contenant le Pad Numérique (Uniquement utilisé sur mobile) */}
                                    {isMobile && (
                                        <Drawer
                                            anchor="bottom"
                                            open={showPad}
                                            onClose={() => setShowPad(false)}
                                            PaperProps={{
                                                sx: {
                                                    borderRadius: '12px 12px 0 0',
                                                    backgroundColor: 'transparent',
                                                    maxWidth: '500px', // Limite la largeur sur desktop
                                                    margin: '0 auto', // Centre sur desktop
                                                },
                                            }}
                                        >
                                            <NumericPad
                                                onKeyPress={key =>
                                                    handlePadKeyPress(key, values.montantFacture, setFieldValue)
                                                }
                                                onConfirm={() => setShowPad(false)}
                                            />{' '}
                                        </Drawer>
                                    )}
                                </Grid>
                                {/*si logged on affiche*/}
                                {!props.logged && (
                                    <Grid item xs={12}>
                                        {/*Champ email*/}
                                        <InputForm required label="Email" type="email" id={'email'} name={'email'} />
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                        {/*actions*/}
                        <CardActions>
                            <Grid container spacing={2}>
                                <Grid item xs={0} sm={7} />
                                <Grid item xs={12} sm={5}>
                                    <Typography variant={'button'} className={'buttons-form'}>
                                        {/*btn annuler*/}
                                        <Button variant="neutral" onClick={props.handlerCancel}>
                                            Annuler
                                        </Button>
                                        {/*Bouton valider, désactivé durant le traitement du formulaire*/}
                                        <Button type="submit" variant="contained" disabled={props.validate}>
                                            Valider
                                        </Button>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardActions>
                    </Form>
                )}
            </Formik>
        </Card>
    );
}

export default ContractAttachementForm;
